/**
 * Shared class strings for the dock.
 *
 * Styled with Tailwind only — no companion stylesheet, no inline style objects.
 * Written as complete literals so Tailwind's JIT scanner picks them up: never
 * build a class name by interpolating a fragment. Concatenating two whole
 * literals (`${OPT_ROW} ${OPT_DIM}`) is fine, both halves exist in the source.
 *
 * The palette is the design's: accent #00A58D (identical to --blue in App.css),
 * dim #949595 (identical to --gray), sheet #121D20, danger #FFB4A8 / #B3382A.
 *
 * The combobox and the switches are PrimeReact Dropdown / InputSwitch in `unstyled`
 * mode, so these constants reach them through their `pt` maps rather than through
 * className. `unstyled` means PrimeReact emits no `p-*` classes at all, so the
 * globally imported mira theme never touches them and there is nothing to override
 * in a stylesheet. Each constant below names the pt section it lands on; see
 * components/Select.tsx and components/Switch.tsx.
 *
 * Two rules the whole file follows, both learned the hard way:
 *
 * 1. Spacing and sizing are arbitrary values (px-[12px], w-[28px]) rather than
 *    scale steps (px-3, w-7). PrimeFlex is imported after Tailwind in App.tsx and
 *    defines many of the same class names with !important and a different scale —
 *    .p-4 is 1.5rem there, .gap-5 is 2rem and .w-7 is 58.33% rather than 28px — so
 *    a scale step silently renders at PrimeFlex's value. Arbitrary values cannot
 *    collide. Font sizes are arbitrary too, for a different reason: a scale step
 *    ships a line-height with the size, while the design leaves line-height at
 *    normal everywhere (its `font:` shorthands). DOCK_ROOT declares leading-[normal]
 *    once and every child inherits it.
 *
 * 2. Two utilities for the same CSS property are resolved by their order in the
 *    generated stylesheet, NOT by their order in the class string — so a "base plus
 *    override" split silently loses. Every property therefore has exactly one owner
 *    among the constants a call site concatenates: CMD_BASE holds only what never
 *    varies, the size constant owns padding and font size, the tone constant owns
 *    colours and weight, and the caller adds cursor-pointer or DISABLED, never both.
 */

export const DISABLED = "opacity-[0.45] cursor-not-allowed";
export const CLICKABLE = "cursor-pointer";

export const DOCK_ROOT =
  "fixed left-0 right-0 bottom-0 max-h-[88vh] flex flex-col bg-[#121D20] text-white leading-[normal] border-t border-white/[0.34] shadow-[0_-14px_44px_rgba(0,0,0,0.5)] z-[9999]";
export const SCRIM = "fixed inset-0 bg-black/45 cursor-pointer z-[9998]";

export const BAR =
  "flex-none min-h-[56px] flex items-center gap-[14px] px-[16px] max-[640px]:flex-wrap max-[640px]:gap-[10px] max-[640px]:py-[10px] max-[640px]:px-[12px]";
export const CHEVRON =
  "w-[28px] h-[28px] rounded-full border border-white/[0.34] bg-transparent text-white text-[11px] leading-none flex-none flex items-center justify-center"; // + CLICKABLE or DISABLED

// commands ------------------------------------------------------------------
export const CMD_BASE = "rounded-[25px] border whitespace-nowrap";
export const CMD_SMALL = "px-[12px] py-[7px] text-[11px]"; // .cmd-s — the two bar commands
export const CMD_FOOT = "px-[14px] py-[8px] text-[11px]"; // .cmd-danger / .cmd-yes / .cmd-no
export const CMD_GHOST = "bg-transparent text-white border-white/85 font-normal";
export const CMD_PRIMARY = "bg-[#00A58D] text-black border-transparent font-medium";
export const CMD_DANGER = "bg-transparent text-[#FFB4A8] border-[#FFB4A8]/50 font-normal";
export const CMD_YES = "bg-[#B3382A] text-white border-transparent font-medium";

// combobox ------------------------------------------------------------------
/**
 * Dropdown's `root` section. Its two accessibility children — a readonly input and
 * a mirror <select> — carry .p-hidden-accessible, whose rule PrimeReact injects at
 * runtime even in unstyled mode, so they are position:absolute and out of this flex
 * row. Only the value span and the trigger take part in it.
 */
export const SEL_BASE =
  "box-border w-auto px-[10px] py-[6px] rounded-[20px] bg-white/[0.06] border border-white/[0.34] text-white text-[12px] font-normal flex items-center justify-between gap-[8px] text-left";
export const SEL_MIN_BAR = "min-w-[150px]"; // .sel-bar
/** Dropdown's `input` section — the visible value, and its hidden twin harmlessly. */
export const SEL_VALUE = "min-w-0 whitespace-nowrap overflow-hidden text-ellipsis";
/** Dropdown's `trigger` section, wrapping the ▾ passed in as dropdownIcon. */
export const CARET = "text-[#949595] text-[11px] flex-none flex items-center";

/**
 * Dropdown's `panel` section. PrimeReact appends the panel to <body> and writes its
 * top/left/min-width as inline styles, flipping it above the trigger when there is
 * no room below — which for this dock is always, it sits on the bottom edge of the
 * viewport. So there are no direction constants here any more, only the look; the
 * z-index has to clear DOCK_ROOT's 9999.
 */
export const OPT_LIST =
  "z-[10000] bg-[#121D20] border border-white/[0.34] rounded-lg shadow-[0_14px_44px_rgba(0,0,0,0.55)] overflow-hidden";
/** Dropdown's `list` section — the <ul>, which needs its browser defaults cleared. */
export const OPT_UL = "list-none m-0 p-0 flex flex-col";
export const OPT_ROW =
  "bg-transparent border-none cursor-pointer text-[12px] font-normal px-[11px] py-[10px] text-left hover:bg-white/10";
export const OPT_SELECTED = "text-[#00A58D] font-semibold";
export const OPT_DIM = "text-[#949595]";
/** keyboard focus inside the list, which Dropdown reports as pt context.focused */
export const OPT_FOCUSED = "bg-white/[0.14]";

// switches ------------------------------------------------------------------
/** InputSwitch's `root` — the track. */
export const SW_BASE =
  "relative w-[42px] h-[22px] rounded-[11px] flex-none p-0 border border-white/[0.34] transition-colors duration-150";
export const SW_ON = "bg-[#00A58D]";
export const SW_OFF = "bg-white/10";
/**
 * InputSwitch's `input` — a real checkbox. Unlike Dropdown's hidden input this one
 * gets no .p-hidden-accessible, so it is hidden here: stretched over the track
 * rather than display:none, so it stays clickable and focusable and `peer` can
 * reach the knob beside it.
 */
export const SW_INPUT = "peer absolute inset-0 w-full h-full m-0 opacity-0";
/** InputSwitch's `slider` — the knob. */
export const SW_KNOB =
  "absolute top-[2px] w-[16px] h-[16px] rounded-full bg-white transition-[left] duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-white/70";
export const SW_KNOB_ON = "left-[22px]";
export const SW_KNOB_OFF = "left-[2px]";

// settings rows -------------------------------------------------------------
export const ROW = "flex-none flex items-center justify-between gap-[12px] text-[12px] font-normal";
export const ROW_LABEL = "text-[#949595]";
export const HINT = "text-[11px] leading-[1.55] font-normal text-[#949595]";

// expanded panel ------------------------------------------------------------
// The lists are appended to <body>, so the panel can always own its own scrolling:
// an open combobox is no longer clipped by this box.
export const PANEL =
  "border-t border-white/[0.16] p-[16px] min-h-0 overflow-y-auto overflow-x-hidden";
export const SPLIT = "flex gap-[20px] items-stretch min-h-[250px] max-[640px]:flex-col max-[640px]:min-h-0";
/**
 * SPLIT without the preview: the settings column alone, full width. The min-h is the
 * running-state height (step header + strip + two rows), so the panel does not jump
 * when a session starts or ends — same magic-number caveat as SPLIT.
 */
export const SPLIT_SOLO = "flex flex-col min-h-[101px]";
export const SPLIT_LEFT = "w-[300px] flex-none flex max-[900px]:w-[250px] max-[640px]:w-full";
export const SPLIT_RIGHT = "flex-1 min-w-0 flex flex-col gap-[15px]";
export const FOOT =
  "flex-none flex flex-wrap items-center justify-end gap-x-[14px] gap-y-[10px] mt-[15px] pt-[13px] border-t border-white/[0.16]";
